// frontend-react/src/components/BoxScore/BoxScorePitching.js
// Pitching statistics table for box score display

import React, { useState } from 'react';
import { dynastyTheme } from '../../services/colorService';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import { DynastyTable } from '../../services/tableService';
import PlayerProfileModal from '../PlayerProfileModal';
import PlayerLink from '../PlayerLink';

/**
 * BoxScorePitching Component
 * Displays pitching statistics in a table format with team totals
 * 
 * @param {Object[]} pitchers - Array of pitcher objects with pitching stats
 * @param {Object} teamTotals - Team totals for pitching stats
 * @param {string} leagueId - League UUID for player modal
 * @param {string} teamId - Team UUID for player modal
 */
const BoxScorePitching = ({ pitchers = [], teamTotals = null, leagueId = null, teamId = null, loading = false }) => {
  // Player modal state
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle player name click
  const handlePlayerClick = (playerId) => {
    setSelectedPlayerId(playerId);
    setIsModalOpen(true);
  };
  // Format innings pitched (handle decimals for outs)
  const formatIP = (ip) => {
    if (ip === null || ip === undefined) return '0.0';
    return ip.toFixed(1);
  };

  // Pitching columns for table — order: IP, H, BB, R, ER, K, ERA, WHIP, W, S, QS
  const columns = [
    {
      key: 'player_name',
      title: 'Player',
      width: 180,
      sortable: true,
      align: 'left',
      sticky: true,
      stickyLeft: 0,
      sortValue: (player) => player.player_name || '',
      render: (player) => (
        <div className="text-left">
          <PlayerLink mlbId={player.player_id} name={player.player_name} style={{ color: '#f9fafb' }} />
          {player.stats?.pitching?.games_started > 0 && (
            <div className={`text-xs ${dynastyTheme.classes.text.primary}`}>
              Starter
            </div>
          )}
        </div>
      )
    },
    {
      key: 'mlb_team',
      title: 'Team',
      width: 80,
      sortable: true,
      sortValue: (player) => player.mlb_team || '',
      render: (player) => (
        <div className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`}>
          {player.mlb_team || '-'}
        </div>
      )
    },
    {
      key: 'innings_pitched',
      title: 'IP',
      width: 80,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.innings_pitched || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {formatIP(player.stats?.pitching?.innings_pitched)}
        </div>
      )
    },
    {
      key: 'hits_allowed',
      title: 'H',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.hits_allowed || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.hits_allowed || 0}
        </div>
      )
    },
    {
      key: 'walks',
      title: 'BB',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.walks_allowed || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.walks_allowed || 0}
        </div>
      )
    },
    {
      key: 'runs_allowed',
      title: 'R',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.runs_allowed || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.runs_allowed || 0}
        </div>
      )
    },
    {
      key: 'earned_runs',
      title: 'ER',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.earned_runs || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.earned_runs || 0}
        </div>
      )
    },
    {
      key: 'strikeouts_pitched',
      title: 'K',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.strikeouts_pitched || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.strikeouts_pitched || 0}
        </div>
      )
    },
    {
      key: 'era',
      title: 'ERA',
      width: 80,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.era || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {formatERA(player.stats?.pitching?.era)}
        </div>
      )
    },
    {
      key: 'whip',
      title: 'WHIP',
      width: 80,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.whip || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.neutralLight}>
          {formatBattingAvg(player.stats?.pitching?.whip)}
        </div>
      )
    },
    {
      key: 'wins',
      title: 'W',
      width: 50,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.wins || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.wins || 0}
        </div>
      )
    },
    {
      key: 'saves',
      title: 'S',
      width: 50,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.saves || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.saves || 0}
        </div>
      )
    },
    {
      key: 'quality_starts',
      title: 'QS',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.pitching?.quality_starts || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.pitching?.quality_starts || 0}
        </div>
      )
    }
  ];

  // Prepare data for table
  const tableData = [...pitchers];

  if (loading) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
          🥎 Pitching
        </h3>
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center gap-3">
            <div 
              className={`w-6 h-6 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`}
            />
            <span className={dynastyTheme.classes.text.neutralLight}>
              Loading pitching stats...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (pitchers.length === 0) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
          🥎 Pitching
        </h3>
        <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
          No pitching stats available for this date
        </div>
      </div>
    );
  }

  return (
    <div className={`${dynastyTheme.components.card.base} p-6`}>
      <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
        🥎 Pitching
      </h3>
      
      <div 
        className="box-score-pitching-wrapper"
        style={{ 
          overflowX: 'auto',
          isolation: 'isolate',
          position: 'relative'
        }}
      >
        <style>{`
          /* Sticky Player column */
          .box-score-pitching-wrapper table thead th:first-child,
          .box-score-pitching-wrapper table tbody td:first-child {
            position: sticky !important;
            left: 0 !important;
            z-index: 10 !important;
            background-color: ${dynastyTheme.tokens.colors.neutral[800]} !important;
            box-shadow: 2px 0 4px rgba(0, 0, 0, 0.3) !important;
          }
          .box-score-pitching-wrapper table tbody tr td:first-child {
            background-color: inherit !important;
          }
        `}</style>
        <DynastyTable
          data={tableData}
          columns={columns}
          maxHeight="600px"
          initialSort={{ key: 'innings_pitched', direction: 'desc' }}
        />
      </div>

      {/* Team Totals Row */}
      {teamTotals && teamTotals.pitching && (
        <div className={`mt-4 p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border-t-2 ${dynastyTheme.classes.border.primary}`}>
          <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-12 gap-4">
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>IP</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{formatIP(teamTotals.pitching.innings_pitched)}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>H</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.hits_allowed || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>R</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.runs_allowed || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>ER</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.earned_runs || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>BB</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.walks_allowed || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>K</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.strikeouts_pitched || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>HR</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.home_runs_allowed || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>W</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.wins || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>SV</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.saves || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>QS</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{teamTotals.pitching.quality_starts || 0}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>ERA</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{formatERA(teamTotals.pitching.era)}</div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>WHIP</div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>{formatBattingAvg(teamTotals.pitching.whip)}</div>
            </div>
          </div>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-2 text-center`}>
            Team Totals
          </div>
        </div>
      )}

      {/* Player Profile Modal */}
      <PlayerProfileModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userTeamId={teamId}
      />
    </div>
  );
};

export default BoxScorePitching;
